import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { io } from 'socket.io-client';
import { useAuth } from './auth.jsx';
import { useI18n } from './i18n/index.jsx';

// Live updates for every open browser (server: live.js).
// - New deploy: the server's build id (Socket.IO "version" on connect,
//   /api/version every 2 minutes and when the tab comes back) differs from
//   this page's __BUILD_ID__ → reload at once, same page and scroll — unless
//   something typed isn't saved yet (a form, an editor, a chat draft): then a
//   banner "A new version is available — Refresh". Typed work is never lost.
// - "content:updated" {area} / "account:updated": pages re-read their data
//   silently (useLive); the account's /me is refreshed and, if the plan, role
//   or rights changed, a toast says so.
// Signed-in users get the events on the chat connection (ChatProvider calls
// emitLive); visitors get their own anonymous connection here.

export const LIVE_EVENT = 'pme:live';
export const emitLive = (type, payload = {}) => window.dispatchEvent(new CustomEvent(LIVE_EVENT, { detail: { type, payload } }));
// useLive(['content', 'news'], fn) — fn runs on those content areas;
// 'account' = the signed-in account changed.
export function useLive(areas, fn) {
  const ref = useRef(fn);
  ref.current = fn;
  const key = [].concat(areas).join(',');
  useEffect(() => {
    const list = key.split(',');
    const on = (e) => {
      const { type, payload } = e.detail || {};
      if ((type === 'content' && list.includes(payload.area)) || (type === 'account' && list.includes('account'))) ref.current(payload);
    };
    window.addEventListener(LIVE_EVENT, on);
    return () => window.removeEventListener(LIVE_EVENT, on);
  }, [key]);
}

// ── Unsaved work ─────────────────────────────────────────────────────────
// Typing in a field marks the page "unsaved" until a save (a write through
// api(): event "pme:saved") or leaving the page. Search boxes and fields
// marked data-live-ignore don't count.
let dirtyAt = 0;
const editable = (el) => !!el && (el.isContentEditable || el.tagName === 'TEXTAREA' || el.tagName === 'SELECT'
  || (el.tagName === 'INPUT' && !['search', 'checkbox', 'radio', 'button', 'submit', 'range', 'color', 'file', 'hidden'].includes(el.type)));
if (typeof document !== 'undefined') {
  document.addEventListener('input', (e) => { const el = e.target; if (editable(el) && !el.closest?.('[data-live-ignore]') && el.type !== 'search') dirtyAt = Date.now(); }, true);
  window.addEventListener('pme:saved', () => { dirtyAt = 0; });
}
// A field being typed in right now, with something in it, counts too.
function hasUnsaved() {
  if (dirtyAt) return true;
  const a = document.activeElement;
  if (!editable(a) || a.type === 'search' || a.closest?.('[data-live-ignore]')) return false;
  return a.isContentEditable ? !!a.textContent.trim() : !!String(a.value || '').trim();
}

// ── Reload keeping the page and the scroll position ─────────────────────
const SCROLL_KEY = 'pme_live_scroll';
const RELOADED_KEY = 'pme_live_reloaded';
const store = {
  get: (k) => { try { return sessionStorage.getItem(k); } catch { return null; } },
  set: (k, v) => { try { if (v === null) sessionStorage.removeItem(k); else sessionStorage.setItem(k, v); } catch { /* private mode */ } },
};
function reloadKeepingPlace(serverVersion) {
  store.set(SCROLL_KEY, JSON.stringify({ path: window.location.pathname + window.location.search, y: window.scrollY }));
  if (serverVersion) store.set(RELOADED_KEY, serverVersion);
  window.location.reload();
}
function restoreScroll() {
  const saved = store.get(SCROLL_KEY);
  if (!saved) return;
  store.set(SCROLL_KEY, null);
  let s;
  try { s = JSON.parse(saved); } catch { return; }
  if (!s || s.path !== window.location.pathname + window.location.search || !s.y) return;
  // The page fills in (data, images) after loading: hold the position for a
  // few seconds — until the visitor scrolls or types themselves.
  const until = Date.now() + 4000;
  let touched = false;
  const stop = () => { touched = true; };
  const evs = ['wheel', 'touchstart', 'keydown', 'mousedown'];
  evs.forEach((e) => window.addEventListener(e, stop, { passive: true, once: true }));
  const tick = () => {
    if (touched || Date.now() > until) { evs.forEach((e) => window.removeEventListener(e, stop)); return; }
    if (Math.abs(window.scrollY - s.y) > 2) window.scrollTo(0, s.y);
    setTimeout(tick, 120);
  };
  tick();
}

const IS_PROD = import.meta.env.PROD;
const MY_VERSION = typeof __BUILD_ID__ !== 'undefined' ? __BUILD_ID__ : 'dev'; // eslint-disable-line no-undef

export function LiveProvider({ children }) {
  const { t } = useI18n();
  const { user, ready, refresh } = useAuth();
  const location = useLocation();
  const [newVersion, setNewVersion] = useState(null);
  const [toast, setToast] = useState('');
  const userRef = useRef(user);
  userRef.current = user;

  useEffect(() => { restoreScroll(); }, []);
  // Leaving a page = its form is gone: nothing unsaved any more.
  useEffect(() => { dirtyAt = 0; }, [location.pathname]);

  const onVersion = useCallback((v) => {
    if (!IS_PROD || !v || v === MY_VERSION) return;
    // Reloaded once already for this version and still old (a cache in the
    // way): don't loop, offer the banner instead.
    if (!hasUnsaved() && store.get(RELOADED_KEY) !== v) reloadKeepingPlace(v);
    else setNewVersion(v);
  }, []);
  // When the work is saved (or the page left), an update waiting can go ahead.
  useEffect(() => {
    if (!newVersion) return undefined;
    const tm = setInterval(() => { if (!hasUnsaved() && store.get(RELOADED_KEY) !== newVersion) reloadKeepingPlace(newVersion); }, 3000);
    return () => clearInterval(tm);
  }, [newVersion]);

  // Version check: now, every 2 minutes, when the tab comes back.
  useEffect(() => {
    const check = () => fetch('/api/version', { cache: 'no-store' }).then((r) => (r.ok ? r.json() : null)).then((d) => d && onVersion(d.version)).catch(() => {});
    check();
    const tm = setInterval(check, 2 * 60 * 1000);
    const vis = () => { if (!document.hidden) check(); };
    document.addEventListener('visibilitychange', vis);
    window.addEventListener('focus', vis);
    return () => { clearInterval(tm); document.removeEventListener('visibilitychange', vis); window.removeEventListener('focus', vis); };
  }, [onVersion]);

  // Events (from ChatProvider's connection, or the visitors' one below).
  useEffect(() => {
    const on = async (e) => {
      const { type, payload } = e.detail || {};
      if (type === 'version') onVersion(payload.version);
      if (type === 'account' && userRef.current) {
        const before = JSON.stringify(accountShape(userRef.current));
        const next = await refresh().catch(() => null);
        if (next && JSON.stringify(accountShape(next)) !== before) setToast(t('live.accountUpdated'));
      }
    };
    window.addEventListener(LIVE_EVENT, on);
    return () => window.removeEventListener(LIVE_EVENT, on);
  }, [onVersion, refresh, t]);

  // Visitors: an anonymous connection for the public events only.
  useEffect(() => {
    if (!ready || user) return undefined;
    const s = io({ path: '/socket.io', transports: ['websocket', 'polling'], reconnectionDelayMax: 20000 });
    s.on('version', (p) => emitLive('version', p));
    s.on('content:updated', (p) => emitLive('content', p));
    return () => { s.close(); };
  }, [ready, !!user]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!toast) return undefined;
    const tm = setTimeout(() => setToast(''), 5000);
    return () => clearTimeout(tm);
  }, [toast]);

  return (
    <>
      {children}
      {newVersion && (
        <div className="live-banner" role="status">
          <span>{t('live.newVersion')}</span>
          <button type="button" className="btn btn-primary btn-sm" onClick={() => reloadKeepingPlace(null)}>{t('live.refresh')}</button>
          <button type="button" className="live-banner-x" aria-label={t('common.close')} onClick={() => setNewVersion(null)}>✕</button>
        </div>
      )}
      {toast && <div className="live-toast" role="status">{toast}</div>}
    </>
  );
}
// What a user sees change in the menus and buttons.
const accountShape = (u) => ({ isSeller: u.isSeller, sellerStatus: u.sellerStatus, complimentary: u.complimentary, role: u.role, team: u.team ? { role: u.team.role, perms: u.team.perms, isOwner: u.team.isOwner } : null });
